interface WindowWithStartDragging {
  startDragging: () => Promise<void>;
}

interface TauriWindowModule {
  getCurrentWindow: () => WindowWithStartDragging;
}

function isElement(target: EventTarget | null): target is Element {
  return target instanceof Element;
}

/** Pre-loaded Tauri window module (null if not in Tauri) */
let tauriWindow: TauriWindowModule | null = null;

// Eagerly load the module at startup so calls in mousedown handlers are synchronous
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
void (async () => {
  try {
    const mod = (await import("@tauri-apps/api/window")) as unknown;
    if (typeof mod === "object" && mod !== null && "getCurrentWindow" in mod) {
      tauriWindow = mod as TauriWindowModule;
    }
  } catch {
    // not in Tauri context
  }
})();

export function shouldSkipWindowDragStart(target: EventTarget | null): boolean {
  if (!isElement(target)) {
    return false;
  }

  return target.closest(
    [
      "button",
      "a",
      "input",
      "textarea",
      "select",
      "[role='button']",
      "[contenteditable='true']",
      "[data-no-window-drag]",
    ].join(","),
  ) !== null;
}

/**
 * Start a window drag. Uses the pre-loaded Tauri module for a synchronous call path.
 * Safe to call in non-Tauri contexts (silently no-ops).
 */
export function startWindowDrag(): void {
  try {
    void tauriWindow?.getCurrentWindow().startDragging();
  } catch {
    // noop: not in Tauri or permission missing
  }
}
